import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { Membership, MembershipLocationSpecificPricingMatrix } from '../../../../../../../../models/membership';
import { MembershipDiscountType, MembershipPurchaseType } from '../../../../../../../../models/membership-enums';
import { CapitanLocation } from '../../../../../../../../models/location';
import { OrganizationConfigService } from '../../../../../../../../services/organization-config/organization-config.service';


@Component({
  selector: 'app-edit-membership-per-location-pricing-matrix-modal',
  templateUrl: './edit-membership-per-location-pricing-matrix-modal.component.html',
  styleUrls: ['./edit-membership-per-location-pricing-matrix-modal.component.css']
})
export class EditMembershipPerLocationPricingMatrixModalComponent implements OnInit {
  @Input() inputPricingMatrix: MembershipLocationSpecificPricingMatrix = null;
  @Input() membership: Membership;
  @Input() locations: CapitanLocation[];
  @Input() completedCallback: (result: MembershipLocationSpecificPricingMatrix) => undefined;

  public pricingMatrix: MembershipLocationSpecificPricingMatrix;

  public errors: any = {};
  public errorMessage = '';

  public readonly MembershipPurchaseType = MembershipPurchaseType;
  public readonly MembershipDiscountType = MembershipDiscountType;

  constructor(
    public activeModal: NgbActiveModal,
    public orgConfig: OrganizationConfigService,
  ) {}

  ngOnInit() {
    this.setInitialPricingMatrix();
  }

  public toggleLocationId(locationId: number) {
    if (this.pricingMatrix.location_ids.includes(locationId)) {
      this.pricingMatrix.location_ids.splice(
        this.pricingMatrix.location_ids.indexOf(locationId),
        1
      );
    } else {
      this.pricingMatrix.location_ids.push(locationId);
    }
  }

  public getDiscountedPrice(basePriceStr: string, discountAmount: number) {
    const basePrice = parseFloat(basePriceStr);

    if (isNaN(basePrice) || (basePrice === null)) { return 0; }

    if (this.membership.discount_type === MembershipDiscountType.currency) {
      return basePrice - discountAmount;
    } else if (this.membership.discount_type === MembershipDiscountType.percentage) {
      return basePrice * (1 - (discountAmount / 100.0));
    } else {
      return 0;
    }
  }

  public addAdultDiscount() {
    const numDiscounts = this.pricingMatrix.adult_discounts.length;
    this.pricingMatrix.adult_discounts.push({
      number_in_tier: numDiscounts + 1,
      discount_amount: this.pricingMatrix.adult_discounts[numDiscounts - 1].discount_amount
    });
  }

  public addYouthDiscount() {
    const numDiscounts = this.pricingMatrix.youth_discounts.length;
    this.pricingMatrix.youth_discounts.push({
      number_in_tier: numDiscounts + 1,
      discount_amount: this.pricingMatrix.youth_discounts[numDiscounts - 1].discount_amount
    });
  }

  public getPricingMatrixAdultNums() {
    let minNumAdults: number;
    if (this.membership.has_youth_tier) {
      minNumAdults = this.membership.minimum_num_adults;
      if (minNumAdults < 0) {
        minNumAdults = 0;
      }
    } else {
      minNumAdults = this.membership.minimum_num_members;
      if (minNumAdults < 1) {
        minNumAdults = 1;
      }
    }

    let maxNumAdults = this.pricingMatrix.adult_discounts.length;
    if (this.membership.allow_unlimited_adults) {
      maxNumAdults++;
    }

    return Array(
      maxNumAdults - minNumAdults + 1
    ).fill(0).map((x, i) => i + minNumAdults);
  }

  public getPricingMatrixYouthNums() {
    let minNumYouths = this.membership.minimum_num_youths;
    if (minNumYouths < 0) {
      minNumYouths = 0;
    }

    let maxNumYouths = this.pricingMatrix.youth_discounts.length;
    if (this.membership.allow_unlimited_youths) {
      maxNumYouths++;
    }

    // Start at 0 youths
    return Array(
      maxNumYouths - minNumYouths + 1
    ).fill(0).map((x, i) => i + minNumYouths);
  }

  public memberNumAllowed(numAdults: number, numYouth: number): boolean {
    if (this.membership.has_youth_tier) {
      if (numAdults < this.membership.minimum_num_adults) { return false; }
      if (numYouth < this.membership.minimum_num_youths) { return false; }
      const numMembers = numAdults + numYouth;
      if (numMembers < 1) { return false; }
      if (numMembers < this.membership.minimum_num_members) { return false; }
    } else {
      if (numAdults < this.membership.minimum_num_members) { return false; }
    }

    return true;
  }

  public getGroupPrice(adultNum: number, youthNum: number) {
    let price = 0;

    if (adultNum > 0) {
      if (this.pricingMatrix.discounts_are_incremental) {
        for (let adultCount = 1; adultCount <= adultNum; adultCount++) {
          price += this.getFeeForAdultCount(adultCount);
        }
      } else {
        price += this.getFeeForAdultCount(adultNum) * adultNum;
      }
    }

    if (youthNum > 0) {
      if (this.pricingMatrix.discounts_are_incremental) {
        for (let youthCount = 1; youthCount <= youthNum; youthCount++) {
          price += this.getFeeForYouthCount(youthCount);
        }
      } else {
        price += this.getFeeForYouthCount(youthNum) * youthNum;
      }
    }

    return price;
  }

  public handleSubmit() {
    this.errors = {};
    this.errorMessage = '';

    if (Object.keys(this.errors).length > 0) {
      this.errorMessage = 'Please correct the errors shown and try again.';
      return;
    }

    this.completedCallback(this.pricingMatrix);
    this.activeModal.close();
  }

  private setInitialPricingMatrix() {
    if (this.inputPricingMatrix) {
      this.pricingMatrix = {
        location_ids: Object.assign([], this.inputPricingMatrix.location_ids),
        membership_fee: this.inputPricingMatrix.membership_fee,
        discount_type: this.inputPricingMatrix.discount_type,
        discounts_are_incremental: this.inputPricingMatrix.discounts_are_incremental,
        discounts_apply_to_individually_frozen_members: this.inputPricingMatrix.discounts_apply_to_individually_frozen_members,
        adult_base_fee: this.inputPricingMatrix.adult_base_fee,
        youth_base_fee: this.inputPricingMatrix.youth_base_fee,
        adult_discounts: null,
        youth_discounts: null,
        initiation_fee: this.inputPricingMatrix.initiation_fee,
        per_customer_add_on_fees: Object.assign({}, this.inputPricingMatrix.per_customer_add_on_fees),
      };
      if (this.inputPricingMatrix.adult_discounts !== null) {
        this.pricingMatrix.adult_discounts = this.inputPricingMatrix.adult_discounts.map(
          disc => Object.assign({}, disc)
        );
      }
      if (this.inputPricingMatrix.youth_discounts !== null) {
        this.pricingMatrix.youth_discounts = this.inputPricingMatrix.youth_discounts.map(
          disc => Object.assign({}, disc)
        );
      }
    } else {
      this.pricingMatrix = {
        location_ids: [],
        membership_fee: (
          (this.membership.membership_fee !== null) ?
          this.membership.membership_fee.toString() :
          null
        ),
        discount_type: this.membership.discount_type,
        discounts_are_incremental: this.membership.discounts_are_incremental,
        discounts_apply_to_individually_frozen_members: this.membership.discounts_apply_to_individually_frozen_members,
        adult_base_fee: (
          (this.membership.adult_base_fee !== null) ?
          this.membership.adult_base_fee.toString() :
          null
        ),
        youth_base_fee: (
          (this.membership.youth_base_fee !== null) ?
          this.membership.youth_base_fee.toString() :
          null
        ),
        adult_discounts: null,
        youth_discounts: null,
        initiation_fee: this.membership.initiation_fee,
        per_customer_add_on_fees: {},
      };
      if (this.membership.adult_discounts !== null) {
        this.pricingMatrix.adult_discounts = this.membership.adult_discounts.map(
          disc => Object.assign({}, disc)
        );
      }
      if (this.membership.youth_discounts !== null) {
        this.pricingMatrix.youth_discounts = this.membership.youth_discounts.map(
          disc => Object.assign({}, disc)
        );
      }
      for (const addOn of this.membership.per_customer_add_ons) {
        this.pricingMatrix.per_customer_add_on_fees[addOn.id] = addOn.fee;
      }
    }
  }

  private getFeeForAdultCount(adultCount: number) {
    let adultDiscount: number;
    if (adultCount <= this.pricingMatrix.adult_discounts.length) {
      adultDiscount = this.pricingMatrix.adult_discounts[adultCount - 1].discount_amount;
    } else {
      const numDis = this.pricingMatrix.adult_discounts.length;
      adultDiscount = this.pricingMatrix.adult_discounts[numDis - 1].discount_amount;
    }
    return this.getDiscountedPrice(this.pricingMatrix.adult_base_fee, adultDiscount);
  }

  private getFeeForYouthCount(youthCount: number) {
    let youthDiscount: number;
    if (youthCount <= this.pricingMatrix.youth_discounts.length) {
      youthDiscount = this.pricingMatrix.youth_discounts[youthCount - 1].discount_amount;
    } else {
      const numDis = this.pricingMatrix.youth_discounts.length;
      youthDiscount = this.pricingMatrix.youth_discounts[numDis - 1].discount_amount;
    }
    return this.getDiscountedPrice(this.pricingMatrix.youth_base_fee, youthDiscount);
  }
}
